import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  supabase,
  isSupabaseConfigured,
  classifySupabaseError,
  reportSchemaError,
} from 'lib/supabase';

// ---------------------------------------------------------------------------
// IEP / behavior goals context.
//
// Wires the "IEP Goals & Objectives Tracking" page to the real `behavior_goals`
// table defined in supabase/migrations/0002_pbis_data_model.sql.
//
// The schema is SMART-structured:
//   goal_area, specific, measurable, baseline, target, mastery_criteria,
//   target_date, progress_percent (0..100), status
//   (not_started / in_progress / mastered / discontinued)
//
// This context mirrors StudentsContext / IncidentsContext:
//   * Live mode  — reads and writes public.behavior_goals via Supabase,
//                  subscribed to realtime changes.
//   * Demo mode  — persists to localStorage so a teacher can still poke the
//                  UI on a machine without Supabase creds.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'edutracker.iep_goals.v1';
const PROGRESS_STORAGE_KEY = 'edutracker.iep_goal_progress.v1';
const TABLE = 'behavior_goals';
const PROGRESS_TABLE = 'goal_progress_entries';

// Columns on `goal_progress_entries` that shipped in a later migration
// (0012_student_self_checkins.sql) and might not exist yet in an older
// Supabase project. If an insert 400s with "column X does not exist"
// for one of these, we surface the schema banner and retry with the
// column stripped so the core numeric data point still saves. Same
// pattern used by IncidentsContext for linked_goal_ids.
const OPTIONAL_LATE_ADDED_PROGRESS_COLUMNS = new Set([
  'source',
  'student_rating',
  'feeling',
]);

const IEPGoalsContext = createContext(null);

function readLocal() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(goals) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(goals));
  } catch {
    // Storage may be full or blocked (Safari private mode); keep going.
  }
}

function generateLocalId() {
  return `goal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function generateLocalEntryId() {
  return `entry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ---------------------------------------------------------------------------
// Progress-entry local storage helpers (demo mode). Shape mirrors the
// public.goal_progress_entries table.
// ---------------------------------------------------------------------------

function readLocalProgress() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(PROGRESS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalProgress(entries) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      PROGRESS_STORAGE_KEY,
      JSON.stringify(entries),
    );
  } catch {
    // Storage may be full or blocked; keep the in-memory state.
  }
}

function normalizeProgressEntry(row) {
  if (!row) return null;
  const rawValue = row.value;
  const rawTarget = row.target_value ?? row.targetValue;
  return {
    id: row.id,
    goalId: row.goal_id ?? row.goalId ?? '',
    studentId: row.student_id ?? row.studentId ?? '',
    observedOn:
      row.observed_on ?? row.observedOn ?? new Date().toISOString().slice(0, 10),
    value: rawValue == null || rawValue === '' ? null : Number(rawValue),
    targetValue:
      rawTarget == null || rawTarget === '' ? null : Number(rawTarget),
    unit: row.unit ?? '',
    measurementType: row.measurement_type ?? row.measurementType ?? null,
    supportLevel: row.support_level ?? row.supportLevel ?? null,
    notes: row.notes ?? '',
    // Student self check-in metadata (migration 0012). Defaults to
    // 'teacher' when absent so old adult-entered rows read as such.
    source: row.source ?? 'teacher',
    studentRating: row.student_rating ?? row.studentRating ?? null,
    feeling: row.feeling ?? null,
    recordedBy: row.recorded_by ?? row.recordedBy ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
    lastUpdated: row.last_updated ?? row.lastUpdated ?? null,
  };
}

function progressToInsertRow(entry) {
  const row = {
    goal_id: entry.goalId,
    student_id: entry.studentId || null,
    observed_on: entry.observedOn || new Date().toISOString().slice(0, 10),
    value:
      entry.value == null || entry.value === '' ? null : Number(entry.value),
    target_value:
      entry.targetValue == null || entry.targetValue === ''
        ? null
        : Number(entry.targetValue),
    unit: entry.unit || null,
    measurement_type: entry.measurementType || null,
    support_level: entry.supportLevel || null,
    notes: entry.notes || null,
  };
  // Only include the late-added columns when the caller actually
  // populated them. That keeps the payload minimal on the common
  // teacher-entered path and prevents a "column does not exist"
  // 400 on old Supabase projects that haven't run migration 0012.
  if (entry.source && entry.source !== 'teacher') row.source = entry.source;
  if (entry.studentRating) row.student_rating = entry.studentRating;
  if (entry.feeling) row.feeling = entry.feeling;
  return row;
}

// Compute progress_percent from the most recent entry the same way the DB
// trigger does. Used for demo-mode goals so the UI stays consistent.
function computeLocalProgress(goal, entriesForGoal) {
  if (!Array.isArray(entriesForGoal) || entriesForGoal.length === 0) {
    return null;
  }
  const sorted = [...entriesForGoal].sort((a, b) => {
    const aDate = a.observedOn || '';
    const bDate = b.observedOn || '';
    if (aDate !== bDate) return aDate < bDate ? 1 : -1;
    const aCreated = a.createdAt || '';
    const bCreated = b.createdAt || '';
    return aCreated < bCreated ? 1 : -1;
  });
  const latest = sorted[0];
  const value = latest?.value;
  const target =
    latest?.targetValue != null && latest.targetValue !== ''
      ? Number(latest.targetValue)
      : goal?.targetValue != null && goal.targetValue !== ''
        ? Number(goal.targetValue)
        : null;
  if (value == null || target == null || target === 0) return null;
  const raw = Math.round((Number(value) / target) * 100);
  return Math.max(0, Math.min(100, raw));
}

// snake_case (Supabase) <-> camelCase (React). Central so callers only ever
// see the app-native shape.
function normalizeIncoming(row) {
  if (!row) return null;
  const rawTargetValue = row.target_value ?? row.targetValue;
  return {
    id: row.id,
    studentId: row.student_id ?? row.studentId ?? '',
    planId: row.plan_id ?? row.planId ?? null,
    goalArea: row.goal_area ?? row.goalArea ?? '',
    specific: row.specific ?? '',
    measurable: row.measurable ?? '',
    baseline: row.baseline ?? '',
    target: row.target ?? '',
    masteryCriteria: row.mastery_criteria ?? row.masteryCriteria ?? '',
    targetDate: row.target_date ?? row.targetDate ?? null,
    progressPercent:
      row.progress_percent ?? row.progressPercent ?? null,
    // Optional numeric metadata used by the goal_progress_entries trigger
    // to auto-compute progress_percent from time-series data. Nullable.
    targetValue:
      rawTargetValue == null || rawTargetValue === '' ? null : Number(rawTargetValue),
    unit: row.unit ?? '',
    status: row.status ?? 'in_progress',
    createdBy: row.created_by ?? row.createdBy ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
    lastUpdated: row.last_updated ?? row.lastUpdated ?? null,
  };
}

function normalizeLocal(rows) {
  return rows.map(normalizeIncoming).filter(Boolean);
}

// Insert row shape. The DB requires goal_area, specific, measurable, target
// to be non-null — the modal enforces the same requirements on the client
// so a submit either succeeds or fails loudly with a clear error.
function toInsertRow(goal) {
  return {
    student_id: goal.studentId,
    plan_id: goal.planId || null,
    goal_area: goal.goalArea,
    specific: goal.specific,
    measurable: goal.measurable,
    baseline: goal.baseline || null,
    target: goal.target,
    mastery_criteria: goal.masteryCriteria || null,
    target_date: goal.targetDate || null,
    progress_percent:
      goal.progressPercent == null || goal.progressPercent === ''
        ? null
        : Number(goal.progressPercent),
    target_value:
      goal.targetValue == null || goal.targetValue === ''
        ? null
        : Number(goal.targetValue),
    unit: goal.unit || null,
    status: goal.status || 'in_progress',
  };
}

// Partial-update row. Only writes the keys the caller explicitly patched, and
// refuses to blank out the four NOT NULL columns.
function toPatchRow(patch) {
  const row = {};
  if (!patch || typeof patch !== 'object') return row;

  const nonEmpty = (key, column) => {
    if (!(key in patch)) return;
    const value = String(patch[key] ?? '').trim();
    if (value) row[column] = value;
  };

  nonEmpty('goalArea', 'goal_area');
  nonEmpty('specific', 'specific');
  nonEmpty('measurable', 'measurable');
  nonEmpty('target', 'target');

  if ('baseline' in patch) row.baseline = patch.baseline || null;
  if ('masteryCriteria' in patch)
    row.mastery_criteria = patch.masteryCriteria || null;
  if ('targetDate' in patch) row.target_date = patch.targetDate || null;
  if ('progressPercent' in patch) {
    row.progress_percent =
      patch.progressPercent == null || patch.progressPercent === ''
        ? null
        : Number(patch.progressPercent);
  }
  if ('targetValue' in patch) {
    row.target_value =
      patch.targetValue == null || patch.targetValue === ''
        ? null
        : Number(patch.targetValue);
  }
  if ('unit' in patch) row.unit = patch.unit || null;
  if ('status' in patch) row.status = patch.status || 'in_progress';
  if ('planId' in patch) row.plan_id = patch.planId || null;

  return row;
}

export function IEPGoalsProvider({ children }) {
  const supabaseLive = isSupabaseConfigured() && supabase !== null;
  const [goals, setGoals] = useState(() =>
    supabaseLive ? [] : normalizeLocal(readLocal()),
  );
  const [loading, setLoading] = useState(supabaseLive);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!supabaseLive) {
      setGoals(normalizeLocal(readLocal()));
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from(TABLE)
      .select('*')
      .order('created_at', { ascending: false });
    if (!mounted.current) return;
    if (fetchError) {
      setError(fetchError.message || 'Failed to load IEP goals');
      setLoading(false);
      return;
    }
    setGoals((data || []).map(normalizeIncoming).filter(Boolean));
    setLoading(false);
  }, [supabaseLive]);

  useEffect(() => {
    if (!supabaseLive) return undefined;
    refresh();
    const channel = supabase
      .channel('public:behavior_goals')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABLE },
        () => refresh(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabaseLive, refresh]);

  const addGoal = useCallback(
    async (input) => {
      const nowIso = new Date().toISOString();
      const draft = normalizeIncoming({
        id: input?.id || generateLocalId(),
        student_id: input?.studentId,
        plan_id: input?.planId,
        goal_area: input?.goalArea,
        specific: input?.specific,
        measurable: input?.measurable,
        baseline: input?.baseline,
        target: input?.target,
        mastery_criteria: input?.masteryCriteria,
        target_date: input?.targetDate,
        progress_percent: input?.progressPercent,
        target_value: input?.targetValue,
        unit: input?.unit,
        status: input?.status || 'in_progress',
        created_at: nowIso,
        last_updated: nowIso,
      });

      if (!supabaseLive) {
        const next = [draft, ...readLocal()];
        writeLocal(next);
        setGoals(next);
        return draft;
      }

      const { data, error: insertError } = await supabase
        .from(TABLE)
        .insert(toInsertRow(draft))
        .select('*')
        .single();
      if (insertError) {
        throw insertError;
      }
      const saved = normalizeIncoming(data);
      setGoals((prev) => [saved, ...prev.filter((g) => g.id !== saved.id)]);
      return saved;
    },
    [supabaseLive],
  );

  const updateGoal = useCallback(
    async (id, patch) => {
      const nowIso = new Date().toISOString();

      if (!supabaseLive) {
        const current = readLocal();
        const next = current.map((g) =>
          g.id === id ? { ...g, ...patch, lastUpdated: nowIso } : g,
        );
        writeLocal(next);
        setGoals(next);
        return next.find((g) => g.id === id) || null;
      }

      const patchRow = toPatchRow(patch);
      if (Object.keys(patchRow).length === 0) {
        return goals.find((g) => g.id === id) || null;
      }
      const { data, error: updateError } = await supabase
        .from(TABLE)
        .update(patchRow)
        .eq('id', id)
        .select('*')
        .single();
      if (updateError) {
        throw updateError;
      }
      const saved = normalizeIncoming(data);
      setGoals((prev) => prev.map((g) => (g.id === id ? saved : g)));
      return saved;
    },
    [supabaseLive, goals],
  );

  const removeGoal = useCallback(
    async (id) => {
      if (!supabaseLive) {
        const next = readLocal().filter((g) => g.id !== id);
        writeLocal(next);
        setGoals(next);
        return true;
      }
      const { error: deleteError } = await supabase
        .from(TABLE)
        .delete()
        .eq('id', id);
      if (deleteError) {
        throw deleteError;
      }
      setGoals((prev) => prev.filter((g) => g.id !== id));
      return true;
    },
    [supabaseLive],
  );

  const goalsForStudent = useCallback(
    (studentId) => goals.filter((g) => g.studentId === studentId),
    [goals],
  );

  // -------------------------------------------------------------------------
  // Progress entries (goal_progress_entries)
  //
  // Time-series data points for a goal. Loaded on-demand by the goal detail
  // view rather than eagerly, so we don't ship every student's entries to
  // the browser on page load. In demo mode, entries live under a companion
  // localStorage key and progress_percent is recomputed client-side to
  // mirror the DB trigger.
  // -------------------------------------------------------------------------

  const listProgressEntries = useCallback(
    async (goalId) => {
      if (!goalId) return [];
      if (!supabaseLive) {
        const all = readLocalProgress().map(normalizeProgressEntry).filter(Boolean);
        return all
          .filter((e) => e.goalId === goalId)
          .sort((a, b) => {
            const aKey = `${a.observedOn || ''}|${a.createdAt || ''}`;
            const bKey = `${b.observedOn || ''}|${b.createdAt || ''}`;
            return bKey.localeCompare(aKey);
          });
      }
      const { data, error: fetchError } = await supabase
        .from(PROGRESS_TABLE)
        .select('*')
        .eq('goal_id', goalId)
        .order('observed_on', { ascending: false })
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      return (data || []).map(normalizeProgressEntry).filter(Boolean);
    },
    [supabaseLive],
  );

  // Bulk fetch: every progress entry, optionally filtered by observed-on
  // date range. Used by Reports & Analytics to roll up data-collection
  // and progress numbers across the whole caseload in a single query
  // instead of one-per-goal. Return shape mirrors listProgressEntries
  // (normalized). In demo mode we read the same localStorage store.
  const listAllProgressEntries = useCallback(
    async ({ from, to } = {}) => {
      if (!supabaseLive) {
        const all = readLocalProgress()
          .map(normalizeProgressEntry)
          .filter(Boolean);
        return all.filter((e) => {
          if (!e.observedOn) return false;
          if (from && String(e.observedOn) < String(from)) return false;
          if (to && String(e.observedOn) > String(to)) return false;
          return true;
        });
      }
      let query = supabase
        .from(PROGRESS_TABLE)
        .select('*')
        .order('observed_on', { ascending: false });
      if (from) query = query.gte('observed_on', from);
      if (to) query = query.lte('observed_on', to);
      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      return (data || []).map(normalizeProgressEntry).filter(Boolean);
    },
    [supabaseLive],
  );

  const logProgress = useCallback(
    async (goalId, entryInput) => {
      if (!goalId) throw new Error('logProgress: goalId is required');

      const parentGoal = goals.find((g) => g.id === goalId);
      const nowIso = new Date().toISOString();
      const draft = normalizeProgressEntry({
        id: entryInput?.id || generateLocalEntryId(),
        goal_id: goalId,
        student_id: entryInput?.studentId || parentGoal?.studentId || '',
        observed_on:
          entryInput?.observedOn ||
          new Date().toISOString().slice(0, 10),
        value: entryInput?.value,
        target_value: entryInput?.targetValue,
        unit: entryInput?.unit || parentGoal?.unit || '',
        measurement_type: entryInput?.measurementType,
        support_level: entryInput?.supportLevel,
        notes: entryInput?.notes,
        // Student self check-in metadata — passed through when the
        // /check-in flow logs a rating on behalf of a student.
        source: entryInput?.source || 'teacher',
        student_rating: entryInput?.studentRating || null,
        feeling: entryInput?.feeling || null,
        created_at: nowIso,
        last_updated: nowIso,
      });

      if (!supabaseLive) {
        const next = [draft, ...readLocalProgress()];
        writeLocalProgress(next);
        const entriesForGoal = next.filter((e) => e.goalId === goalId);
        const nextProgress = computeLocalProgress(parentGoal, entriesForGoal);
        setGoals((prev) => {
          const updated = prev.map((g) =>
            g.id === goalId
              ? { ...g, progressPercent: nextProgress, lastUpdated: nowIso }
              : g,
          );
          writeLocal(updated);
          return updated;
        });
        return draft;
      }

      // First attempt sends the full row including migration-0012
      // columns (source / student_rating / feeling). Older Supabase
      // projects that haven't applied that migration will 400 with
      // "column X does not exist" — we surface a schema banner AND
      // retry with the offending column stripped so the core numeric
      // data point still saves. Same fallback pattern as
      // IncidentsContext uses for linked_goal_ids.
      const attempt = async (row) =>
        supabase.from(PROGRESS_TABLE).insert(row).select('*').single();

      let row = progressToInsertRow(draft);
      let { data, error: insertError } = await attempt(row);

      while (insertError) {
        const classified = classifySupabaseError(insertError);
        const canStrip =
          classified?.kind === 'missing_column' &&
          classified.column &&
          OPTIONAL_LATE_ADDED_PROGRESS_COLUMNS.has(classified.column) &&
          Object.prototype.hasOwnProperty.call(row, classified.column);
        if (!canStrip) break;
        reportSchemaError(insertError, {
          context: 'goal_progress_entries.insert',
        });
        const { [classified.column]: _dropped, ...trimmed } = row;
        row = trimmed;
        ({ data, error: insertError } = await attempt(row));
      }
      if (insertError) throw insertError;

      // The DB trigger recomputes behavior_goals.progress_percent, but the
      // client's cached copy is stale. Refetch just this goal so the card /
      // detail view show the new value immediately without waiting for the
      // realtime channel.
      const { data: goalRow, error: refetchError } = await supabase
        .from(TABLE)
        .select('*')
        .eq('id', goalId)
        .single();
      if (!refetchError && goalRow) {
        const saved = normalizeIncoming(goalRow);
        setGoals((prev) => prev.map((g) => (g.id === goalId ? saved : g)));
      }

      return normalizeProgressEntry(data);
    },
    [supabaseLive, goals],
  );

  const removeProgressEntry = useCallback(
    async (entryId, goalId) => {
      if (!entryId) return false;
      if (!supabaseLive) {
        const current = readLocalProgress();
        const next = current.filter((e) => e.id !== entryId);
        writeLocalProgress(next);
        const parentGoal = goals.find((g) => g.id === goalId);
        const entriesForGoal = next
          .map(normalizeProgressEntry)
          .filter(Boolean)
          .filter((e) => e.goalId === goalId);
        const nextProgress = computeLocalProgress(parentGoal, entriesForGoal);
        const nowIso = new Date().toISOString();
        setGoals((prev) => {
          const updated = prev.map((g) =>
            g.id === goalId
              ? { ...g, progressPercent: nextProgress, lastUpdated: nowIso }
              : g,
          );
          writeLocal(updated);
          return updated;
        });
        return true;
      }
      const { error: deleteError } = await supabase
        .from(PROGRESS_TABLE)
        .delete()
        .eq('id', entryId);
      if (deleteError) throw deleteError;

      if (goalId) {
        const { data: goalRow, error: refetchError } = await supabase
          .from(TABLE)
          .select('*')
          .eq('id', goalId)
          .single();
        if (!refetchError && goalRow) {
          const saved = normalizeIncoming(goalRow);
          setGoals((prev) => prev.map((g) => (g.id === goalId ? saved : g)));
        }
      }
      return true;
    },
    [supabaseLive, goals],
  );

  const value = useMemo(
    () => ({
      goals,
      loading,
      error,
      isLive: supabaseLive,
      refresh,
      addGoal,
      updateGoal,
      removeGoal,
      goalsForStudent,
      listProgressEntries,
      listAllProgressEntries,
      logProgress,
      removeProgressEntry,
    }),
    [
      goals,
      loading,
      error,
      supabaseLive,
      refresh,
      addGoal,
      updateGoal,
      removeGoal,
      goalsForStudent,
      listProgressEntries,
      listAllProgressEntries,
      logProgress,
      removeProgressEntry,
    ],
  );

  return (
    <IEPGoalsContext.Provider value={value}>{children}</IEPGoalsContext.Provider>
  );
}

export function useIEPGoals() {
  const ctx = useContext(IEPGoalsContext);
  if (!ctx) {
    throw new Error('useIEPGoals must be used within an <IEPGoalsProvider>');
  }
  return ctx;
}
